import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { poolTimingEntries } from "../../lib/lab-api";
import {
    Users, MagnifyingGlass, X, ChartBar, Warning, Sparkle, Trash, Wallet,
    Trophy, Snowflake, Flag, Lightning, PlayCircle, Wrench, Compass, Plus, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import {
    timingCurrentFpl, timingPoolMatch, timingPoolAnalyze, timingPoolOptimize,
    timingPoolSuggest,
} from "../../lib/timing-pool-api";
import { t, PERIODS } from "../../lib/timing-i18n";
import { PositionPill } from "./TimingBits";
import { useListKeyboardNav } from "../../lib/use-list-keyboard-nav";
import { POOL_MAX, POOL_ANALYZE_SIZE } from "../../lib/timing-pool-store";
import { SavedLists } from "./SavedLists";
import { track } from "../../lib/analytics";

const BUDGET_LIMIT = 200.0;

// Strip diacritics so "dubra" matches "Dúbravka", "haaland" matches "Håland" atd.
const stripDia = (s) =>
    (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();


function PositionBar({ pool, lang, activePos, onTogglePos }) {
    const counts = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
    for (const p of pool) {
        if (counts[p.position] !== undefined) counts[p.position] += 1;
    }
    const POS_CLR = {
        GKP: "text-fa-yellow border-fa-yellow/40",
        DEF: "text-fa-mint border-fa-mint/40",
        MID: "text-fa-sky border-fa-sky/40",
        FWD: "text-rose-300 border-rose-300/40",
    };
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-center gap-3 flex-wrap"
             data-testid="pool-position-bar">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                Pozice
            </span>
            {["GKP", "DEF", "MID", "FWD"].map((pos) => {
                const active = activePos === pos;
                return (
                    <button
                        key={pos}
                        type="button"
                        onClick={() => onTogglePos(pos)}
                        data-testid={`pos-slot-${pos}`}
                        title={active ? "Zrušit filtr pozice" : `Zobrazit jen ${pos} ve watchlistu`}
                        className={`flex items-center gap-1.5 px-2 py-1 border ${POS_CLR[pos]} font-mono text-[11px] tracking-widest cursor-pointer hover:brightness-125 ${active ? "ring-2 ring-fa-cream ring-offset-1 ring-offset-black" : ""}`}
                    >
                        <span className="font-bold">{pos}</span>
                        <span className="tabular-nums">{counts[pos]}</span>
                    </button>
                );
            })}
            {activePos && (
                <button
                    type="button"
                    onClick={() => onTogglePos(activePos)}
                    data-testid="pos-filter-clear"
                    className="font-mono text-[9px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black"
                >
                    Filtr: {activePos} ✕
                </button>
            )}
            <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40">
                Watchlist · složení libovolné · 30 pro simulaci vybereš v Simulátoru
            </span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Small subcomponents kept inline — this feature is one cohesive workspace.
// ---------------------------------------------------------------------------

function BudgetBar({ total, count, lang }) {
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="pool-budget-bar">
            <div className="flex items-center gap-3 flex-wrap font-mono text-[10px] uppercase tracking-widest">
                <span className="text-fa-sky flex items-center gap-1.5">
                    <Wallet size={11} weight="fill" /> Pool
                </span>
                <span className="text-white/50">
                    {t("poolCount", lang)}: <strong className="text-white">{count}</strong> / {POOL_MAX}
                </span>
                <span className="text-white/50">
                    {t("poolAvgPrice", lang)}: <strong className="text-white">£{count ? (total / count).toFixed(2) : "0.00"}m</strong>
                </span>
                <span className="text-white/50">
                    Součet: <strong className="text-fa-yellow">£{total.toFixed(1)}m</strong>
                </span>
            </div>
        </div>
    );
}

function PlayerSearchRow({ p, alreadyIn, onAdd, highlighted, innerRef }) {
    return (
        <button
            type="button"
            ref={innerRef}
            onClick={() => !alreadyIn && onAdd(p)}
            disabled={alreadyIn}
            data-testid={`pool-search-add-${p.fpl_id}`}
            className={`w-full text-left px-2 py-1.5 border-b border-white/5 last:border-0 flex items-center justify-between gap-2 ${alreadyIn ? "opacity-40 cursor-not-allowed bg-white/[0.02]" : highlighted ? "bg-fa-sky/20" : "hover:bg-fa-sky/10"}`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <span className="font-display font-black text-sm truncate">{p.full_name || p.web_name}</span>
                <PositionPill positions={[p.position]} />
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50 truncate">{p.team}</span>
                {alreadyIn && (
                    <span className="font-mono text-[8px] uppercase tracking-widest px-1 py-0.5 border border-fa-mint/50 text-fa-mint">
                        ✓ v poolu
                    </span>
                )}
            </div>
            <span className="font-mono text-[11px] tabular-nums text-fa-yellow font-bold whitespace-nowrap">{p.price == null ? "£—" : `£${p.price.toFixed(1)}m`}</span>
        </button>
    );
}

function PoolChip({ p, onRemove }) {
    return (
        <div
            className="flex items-center gap-1.5 border border-fa-sky/40 bg-fa-sky/5 px-2 py-1"
            data-testid={`pool-chip-${p.fpl_id}`}
        >
            <span className="font-display font-black text-[13px]">{p.display_name}</span>
            <span className="font-mono text-[9px] text-white/50">{p.position}</span>
            <span className="font-mono text-[10px] tabular-nums text-fa-yellow font-bold">{p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}`}</span>
            <button
                type="button"
                onClick={() => onRemove(p.fpl_id)}
                data-testid={`pool-remove-${p.fpl_id}`}
                className="text-white/40 hover:text-red-300"
            >
                <X size={11} weight="bold" />
            </button>
        </div>
    );
}

function DnaChart({ dna, lang }) {
    const data = PERIODS.map((p) => ({
        phase: t(p.labelKey, lang).split(" ").slice(0, 2).join(" "),
        score: dna.phase_strengths?.[p.key] ?? 0,
    }));
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="pool-dna-chart">
            <div className="flex items-center gap-2 mb-2">
                <ChartBar size={12} weight="fill" className="text-fa-sky" />
                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                    {t("phaseStrength", lang)}
                </h3>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-fa-yellow tabular-nums">
                    DNA {dna.average_dna?.toFixed(1) ?? "—"}/100
                </span>
            </div>
            <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="phase" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 10 }} />
                        <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 10 }} domain={[0, 100]} />
                        <Tooltip
                            wrapperStyle={{ outline: "none" }}
                            contentStyle={{
                                background: "#0d0d0d",
                                border: "1px solid rgba(255,255,255,0.15)",
                                fontFamily: "IBM Plex Mono, monospace",
                                fontSize: 11,
                                color: "#fff",
                            }}
                            labelStyle={{ color: "#FFF3C2", fontWeight: 700 }}
                            itemStyle={{ color: "#fff" }}
                        />
                        <ReferenceLine y={60} stroke="rgba(158, 232, 180, 0.4)" strokeDasharray="4 3" />
                        <Bar dataKey="score" fill="#9DB7F5" radius={[2, 2, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function TypologyRow({ typology, lang }) {
    if (!typology || !typology.length) return null;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">{t("typology", lang)}:</span>
            {typology.map((k) => {
                const strKey = `typo_${k}`;
                return (
                    <span
                        key={k}
                        data-testid={`typology-${k}`}
                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-fa-sky/40 bg-fa-sky/5 text-fa-sky"
                    >
                        {t(strKey, lang)}
                    </span>
                );
            })}
        </div>
    );
}

function SuggestionsBlock({ suggestions, context, onAdd, lang }) {
    if (!suggestions?.length) return null;
    return (
        <div className="border border-fa-sky/40 bg-fa-sky/[0.03] p-4" data-testid="pool-suggestions">
            <div className="flex items-center gap-2 mb-2">
                <Compass size={13} weight="fill" className="text-fa-sky" />
                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                    Doporučení na doplnění poolu
                </h3>
                <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/45">
                    {context?.weak_phases?.length > 0 && (
                        <>Slabá fáze: <strong className="text-fa-sky">{context.weak_phases.map(([k]) => k.replace("gw", "GW").replace("_", "-")).join(" · ")}</strong></>
                    )}
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {suggestions.map((s) => {
                    const c = s.candidate;
                    return (
                        <div
                            key={c.fpl_id}
                            data-testid={`suggestion-${c.fpl_id}`}
                            className="border border-white/10 bg-[#0d0d0d] p-2.5 space-y-1.5"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="font-display font-black text-sm truncate">{c.display_name}</div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 truncate">
                                        {c.team} · {c.position} · {c.current_price == null ? "£—" : `£${c.current_price.toFixed(1)}m`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onAdd(s)}
                                    data-testid={`suggestion-add-${c.fpl_id}`}
                                    className="font-mono text-[9px] uppercase tracking-widest bg-fa-sky text-black px-2 py-1 hover:bg-fa-sky/80 font-bold flex items-center gap-1"
                                >
                                    <Plus size={10} weight="bold" />
                                    Přidat
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {s.reasons.map((r) => (
                                    <span
                                        key={r}
                                        className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-fa-sky/40 text-fa-sky bg-fa-sky/5"
                                    >
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TopList({ title, icon: Icon, players, lang, accent = "fa-sky", testid, scoreKey = "season_dna" }) {
    if (!players || !players.length) return null;
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={11} weight="fill" className={`text-${accent}`} />
                <h4 className="font-display font-black text-xs uppercase tracking-widest">{title}</h4>
            </div>
            <ol className="space-y-1">
                {players.map((p, i) => (
                    <li key={p.player_key} className="flex items-center justify-between font-mono text-[11px]">
                        <span className="text-white/50 tabular-nums w-4">{i + 1}</span>
                        <span className="flex-1 font-display font-black text-sm truncate">{p.display_name}</span>
                        <span className="text-white/40 tabular-nums ml-2">{p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}`}</span>
                        <span className="text-fa-yellow tabular-nums w-10 text-right font-bold">{(p.scores[scoreKey] ?? p.scores.season_dna)?.toFixed(0)}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function MetricsGlossary({ lang }) {
    const items = [
        { k: "season_dna", label: "Season DNA", color: "text-fa-yellow border-fa-yellow/40 bg-fa-yellow/[0.04]",
          desc: "Kombinovaný sezónní score 0-100. Váhy: 40 % síla fází + 25 % konzistence + 20 % hodnota + 10 % dostupnost + 5 % trend. Vyšší = lepší celkově." },
        { k: "consistency", label: "Konzistence", color: "text-fa-mint border-fa-mint/40 bg-fa-mint/[0.04]",
          desc: "Odměna za vysoký floor + nízkou variabilitu. 0.35·return-rate + 0.20·pokrytí + 0.20·(1−blank) + 0.25·(1−CV). Vyšší = stabilnější." },
        { k: "explosiveness", label: "Výbušnost", color: "text-fa-yellow border-fa-yellow/40 bg-fa-yellow/[0.04]",
          desc: "% odehraných GW s hauly ≥10 bodů + peak. Vyšší = víc velkých kol (kapitánský potenciál)." },
        { k: "value", label: "Hodnota", color: "text-fa-sky border-fa-sky/40 bg-fa-sky/[0.04]",
          desc: "Body na £m — PPG dělené aktuální cenou, škálováno k elitnímu benchmarku 1.5 pts/£m. Vyšší = víc bodů za peníze." },
        { k: "season_holder", label: "Držák sezóny", color: "text-fa-mint border-fa-mint/40 bg-fa-mint/[0.04]",
          desc: "Celosezónní důvěryhodnost — kombinuje konzistenci, pokrytí a minimální rozptyl. Vyšší = víc \"set-and-forget\" typ." },
    ];
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="pool-metrics-glossary">
            <div className="flex items-center gap-2 mb-2">
                <Info size={12} weight="fill" className="text-fa-sky" />
                <h4 className="font-display font-black text-xs uppercase tracking-widest">
                    Co znamenají skóre
                </h4>
                <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40">
                    Všechna skóre 0–100 · vyšší = lepší
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                {items.map((it) => (
                    <div
                        key={it.k}
                        data-testid={`glossary-${it.k}`}
                        className={`border ${it.color} p-2 space-y-1`}
                    >
                        <div className="font-display font-black text-xs uppercase tracking-widest">{it.label}</div>
                        <p className="font-mono text-[9.5px] leading-relaxed text-white/70">{it.desc}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PlayerCard({ p, lang }) {
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-2" data-testid={`player-card-${p.fpl_id}`}>
            <div className="flex items-center justify-between gap-2">
                <div>
                    <Link to={`/players/${encodeURIComponent(p.historical_slug || p.fpl_id)}`}
                          data-testid={`player-card-profile-${p.fpl_id}`}
                          className="font-display font-black text-base hover:text-fa-mint transition-colors">
                        {p.display_name}
                    </Link>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                        {p.team} · {p.position} · {p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}m`} · {p.seasons_available}× seas
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-[8px] uppercase tracking-widest text-white/40">DNA</div>
                    <div className="font-display font-black text-xl text-fa-yellow tabular-nums">{p.scores.season_dna?.toFixed(0)}</div>
                    {p.scores.season_holder != null && (
                        <div className="font-mono text-[8px] uppercase tracking-widest text-white/50 tabular-nums">
                            HOLDER <span className={`${p.scores.season_holder >= 60 ? "text-fa-mint font-bold" : "text-white/70"}`}>{p.scores.season_holder.toFixed(0)}</span>
                        </div>
                    )}
                </div>
            </div>
            {/* Score bars */}
            <div className="grid grid-cols-3 gap-1">
                {[
                    ["consistency", p.scores.consistency, "#9EE8B4"],
                    ["explosiveness", p.scores.explosiveness, "#FFF3C2"],
                    ["value", p.scores.value, "#9DB7F5"],
                ].map(([key, val, color]) => (
                    <div key={key}>
                        <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-widest text-white/45">
                            <span>{t(key, lang).slice(0, 5)}</span>
                            <span className="tabular-nums text-white/85">{val?.toFixed(0) ?? "—"}</span>
                        </div>
                        <div className="h-1 bg-white/[0.05]">
                            <div className="h-full" style={{ width: `${Math.min(val || 0, 100)}%`, background: color }} />
                        </div>
                    </div>
                ))}
            </div>
            {/* Timing sparklines */}
            <div className="flex gap-0.5 h-6 items-end">
                {PERIODS.map((ph) => {
                    const v = p.scores.timing?.[ph.key] ?? 0;
                    const h = Math.max(4, (v / 100) * 100);
                    const isBest = p.best_phase === ph.key;
                    return (
                        <div key={ph.key} className="flex-1 flex flex-col items-center gap-0.5" title={`${t(ph.labelKey, lang)}: ${v.toFixed(0)}`}>
                            <div
                                className={`w-full ${isBest ? "bg-fa-yellow" : "bg-fa-sky/60"}`}
                                style={{ height: `${h}%` }}
                            />
                            <span className="font-mono text-[7px] text-white/40 tracking-tight">{ph.gw[0]}-{ph.gw[1]}</span>
                        </div>
                    );
                })}
            </div>
            {/* Labels */}
            <div className="flex flex-wrap gap-1">
                {p.labels.map((l) => (
                    <span
                        key={l}
                        className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-fa-sky/40 text-fa-sky bg-fa-sky/5"
                    >
                        {l}
                    </span>
                ))}
            </div>
            {/* Verdict */}
            <p className="font-mono text-[10px] leading-relaxed text-white/70 border-t border-white/10 pt-2">
                {p.verdict}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Pool Analyzer main component
// ---------------------------------------------------------------------------

export default function PoolAnalyzer({ lang, sharedPool, onSwitchTab }) {
    const [fpl, setFpl] = useState(null); // { players: [], team_count }
    const [loadingFpl, setLoadingFpl] = useState(true);
    const [query, setQuery] = useState("");
    // pool state now comes from the shared hook — persistent + cross-tab.
    const { pool, setPool, addPlayer: sharedAdd, removePlayer: sharedRemove, clearPool: sharedClear } = sharedPool;
    const { user } = useAuth();
    const syncedRef = useRef(false);

    // Sjednocení poolů: serverový Scout pool → Team DNA (jednosměrný merge při načtení).
    useEffect(() => {
        if (!user || syncedRef.current) return;
        syncedRef.current = true;
        poolTimingEntries().then((d) => {
            let added = 0;
            (d.items || []).forEach((it) => {
                if (!it.historical_slug) return;
                if (pool.some((p) => p.historical_slug === it.historical_slug)) return;
                const ok = sharedAdd({
                    fpl_id: it.fpl_id, display_name: it.display_name,
                    historical_slug: it.historical_slug, position: it.position,
                    team: it.team, current_price: it.current_price,
                    ownership_pct: it.ownership_pct, seasons_available: it.seasons_available,
                });
                if (ok) added += 1;
            });
            if (added > 0) toast.success(`Načteno ${added} hráčů z tvého Scout poolu`);
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);
    const [pasteText, setPasteText] = useState("");
    const [showPaste, setShowPaste] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [busy, setBusy] = useState(false);
    // Sort + filter state for the analyzed player-cards grid.
    const [sortBy, setSortBy] = useState("season_dna");
    const [sortDir, setSortDir] = useState("desc");
    const [posFilter, setPosFilter] = useState("");
    const [chipPosFilter, setChipPosFilter] = useState("");

    // Load current FPL bootstrap on mount
    useEffect(() => {
        setLoadingFpl(true);
        timingCurrentFpl()
            .then(setFpl)
            .catch(() => toast.error("FPL bootstrap failed"))
            .finally(() => setLoadingFpl(false));
    }, []);

    const filteredCurrent = useMemo(() => {
        if (!fpl?.players || !query) return [];
        const q = stripDia(query.trim());
        if (!q) return [];
        return fpl.players
            .filter((p) =>
                stripDia(p.web_name).includes(q)
                || stripDia(p.full_name).includes(q)
                || stripDia(p.team).includes(q)
                || stripDia(p.second_name).includes(q)
            )
            .slice(0, 20);
    }, [fpl, query]);

    // Keyboard navigation for the search dropdown.
    // Enter picks the highlighted result and adds it to the pool.
    const { selectedIndex, onKeyDown: searchKeyDown, itemRef, resetIndex } = useListKeyboardNav({
        itemsLength: filteredCurrent.length,
        onEnter: (i) => {
            const p = filteredCurrent[i];
            if (!p) return;
            if (pool.some((x) => x.fpl_id === p.fpl_id)) return;
            addCurrent(p);
        },
        onEscape: () => setQuery(""),
    });

    // Reset highlight whenever the query changes.
    useEffect(() => { resetIndex(); }, [query, resetIndex]);

    // Sort + filter the analyzed players.
    const sortedPlayers = useMemo(() => {
        if (!analysis?.players) return [];
        const list = analysis.players.filter((p) => !posFilter || p.position === posFilter);
        const getVal = (p) => {
            if (sortBy === "display_name") return (p.display_name || "").toLowerCase();
            if (sortBy === "current_price") return p.current_price ?? 0;
            return p.scores?.[sortBy] ?? -Infinity;
        };
        const sorted = [...list].sort((a, b) => {
            const va = getVal(a), vb = getVal(b);
            if (va === vb) return 0;
            const cmp = va < vb ? -1 : 1;
            return sortDir === "asc" ? cmp : -cmp;
        });
        return sorted;
    }, [analysis, sortBy, sortDir, posFilter]);

    const budget = useMemo(() => {
        const total = pool.reduce((a, p) => a + (p.current_price || 0), 0);
        return { total, over: total > BUDGET_LIMIT };
    }, [pool]);

    const addCurrent = async (currentPlayer) => {
        // Functional dedupe — protects against rapid double-click.
        if (pool.find((p) => p.fpl_id === currentPlayer.fpl_id)) {
            toast.info("Hráč už v poolu je");
            return;
        }
        if (pool.length >= POOL_MAX) {
            toast.error(`Max ${POOL_MAX} hráčů`);
            return;
        }
        setBusy(true);
        try {
            const r = await timingPoolMatch([{ fpl_id: currentPlayer.fpl_id }]);
            const m = r.matches?.[0];
            const c = m?.current || currentPlayer;
            setPool((cur) => {
                if (cur.find((p) => p.fpl_id === c.fpl_id)) return cur; // final guard
                if (cur.length >= POOL_MAX) return cur;
                return [...cur, {
                    fpl_id: c.fpl_id,
                    display_name: c.display_name || currentPlayer.full_name,
                    historical_slug: m?.historical_slug,
                    position: c.position,
                    team: c.team,
                    current_price: c.price,
                    ownership_pct: c.ownership_pct,
                    seasons_available: m?.seasons_available || 0,
                    match_confidence: m?.confidence || 0,
                }];
            });
            // Keep dropdown open so users can add multiple players from the same
            // team/query without re-typing. Escape or edit clears the query.
        } catch (e) {
            toast.error("Match failed");
        } finally {
            setBusy(false);
        }
    };

    const importPaste = async () => {
        const lines = pasteText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        if (lines.length === 0) return;
        setBusy(true);
        try {
            const r = await timingPoolMatch(lines.map((query) => ({ query })));
            const additions = [];
            const unmatched = [];
            const dupes = [];
            const seenIds = new Set(pool.map((p) => p.fpl_id));
            for (const m of r.matches || []) {
                if (m.current) {
                    if (seenIds.has(m.current.fpl_id)) {
                        dupes.push(m.current.display_name || m.input);
                        continue;
                    }
                    if (pool.length + additions.length >= POOL_MAX) break;
                    seenIds.add(m.current.fpl_id);
                    additions.push({
                        fpl_id: m.current.fpl_id,
                        display_name: m.current.display_name,
                        historical_slug: m.historical_slug,
                        position: m.current.position,
                        team: m.current.team,
                        current_price: m.current.price,
                        ownership_pct: m.current.ownership_pct,
                        seasons_available: m.seasons_available || 0,
                        match_confidence: m.confidence || 0,
                    });
                } else {
                    unmatched.push(m.input);
                }
            }
            setPool((cur) => {
                const seen = new Set(cur.map((p) => p.fpl_id));
                const clean = additions.filter((a) => !seen.has(a.fpl_id));
                return [...cur, ...clean];
            });
            const msgs = [];
            if (additions.length) msgs.push(`Přidáno ${additions.length}`);
            if (dupes.length) msgs.push(`Duplicity vynechány: ${dupes.slice(0, 4).join(", ")}${dupes.length > 4 ? "…" : ""}`);
            if (unmatched.length) msgs.push(`Nenalezeno: ${unmatched.slice(0, 4).join(", ")}${unmatched.length > 4 ? "…" : ""}`);
            if (msgs.length) {
                if (unmatched.length) toast.error(msgs.join(" · "));
                else if (dupes.length) toast.info(msgs.join(" · "));
                else toast.success(msgs.join(" · "));
            }
            setPasteText("");
            setShowPaste(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const removeFromPool = (fplId) => {
        sharedRemove(fplId);
        setAnalysis(null);
        setSuggestions(null);
    };

    const clearPool = () => {
        sharedClear();
        setAnalysis(null);
        setSuggestions(null);
    };

    const addSuggestion = (s) => {
        const c = s.candidate;
        if (pool.find((p) => p.fpl_id === c.fpl_id)) {
            toast.info("Hráč už v poolu je");
            return;
        }
        if (pool.length >= POOL_MAX) {
            toast.error(`Max ${POOL_MAX} hráčů`);
            return;
        }
        setPool((cur) => [...cur, {
            fpl_id: c.fpl_id,
            display_name: c.display_name,
            historical_slug: c.player_key,
            position: c.position,
            team: c.team,
            current_price: c.current_price,
            ownership_pct: 0,
            seasons_available: 0,
            match_confidence: 100,
        }]);
        // Remove from suggestions list so it doesn't stay clickable
        setSuggestions((cur) => cur ? {
            ...cur,
            suggestions: cur.suggestions.filter((x) => x.candidate.fpl_id !== c.fpl_id),
        } : cur);
        toast.success(`Přidán ${c.display_name}`);
    };

    const analyze = async () => {
        if (pool.length < 5) {
            toast.error(`Analýza vyžaduje alespoň 5 hráčů — přidej ještě ${5 - pool.length}.`);
            return;
        }
        setBusy(true);
        try {
            const payload = pool.map((p) => ({
                fpl_id: p.fpl_id,
                historical_slug: p.historical_slug,
                display_name: p.display_name,
                position: p.position,
                team: p.team,
                current_price: p.current_price,
                ownership_pct: p.ownership_pct,
                availability_pct: 100,
            }));
            const d = await timingPoolAnalyze(payload);
            setAnalysis(d);
            setSuggestions(null); // reset suggestions when re-analyzing
            try {
                track("pool_analyze", {
                    tool: "timing",
                    meta: {
                        pool_size: pool.length,
                        player_ids: payload.map((p) => p.fpl_id).filter(Boolean),
                        season_dna: d?.dna?.season_dna || null,
                    },
                });
            } catch { /* silent */ }
            toast.success("Analýza hotová");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analyze failed");
        } finally {
            setBusy(false);
        }
    };

    const fetchSuggestions = async () => {
        if (pool.length < 2) {
            toast.error("Nejprve sestav pool");
            return;
        }
        setBusy(true);
        try {
            const payload = pool.map((p) => ({
                fpl_id: p.fpl_id,
                historical_slug: p.historical_slug,
                display_name: p.display_name,
                position: p.position,
                team: p.team,
                current_price: p.current_price,
                ownership_pct: p.ownership_pct,
                availability_pct: 100,
            }));
            const d = await timingPoolSuggest(payload, 10);
            setSuggestions(d);
            if (!d.suggestions?.length) toast.info("Žádná zjevná doporučení pro tento pool");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suggest failed");
        } finally {
            setBusy(false);
        }
    };

    const optimize = async () => {
        if (pool.length < 5) {
            toast.error("Přidej alespoň 5 kandidátů");
            return;
        }
        setBusy(true);
        try {
            const payload = pool.map((p) => ({
                fpl_id: p.fpl_id,
                historical_slug: p.historical_slug,
                display_name: p.display_name,
                position: p.position,
                team: p.team,
                current_price: p.current_price,
                ownership_pct: p.ownership_pct,
                availability_pct: 100,
            }));
            const d = await timingPoolOptimize(payload);
            // Reset pool to just the selected players
            const selected = d.selected.map((s) => ({
                fpl_id: s.fpl_id,
                display_name: s.display_name,
                historical_slug: s.player_key,
                position: s.position,
                team: pool.find((pp) => pp.fpl_id === s.fpl_id)?.team,
                current_price: s.current_price,
                ownership_pct: pool.find((pp) => pp.fpl_id === s.fpl_id)?.ownership_pct,
                seasons_available: pool.find((pp) => pp.fpl_id === s.fpl_id)?.seasons_available || 0,
            }));
            setPool(selected);
            toast.success(`Optimalizováno: ${d.selected.length} hráčů za £${d.budget.total_price}m`);
            // Re-analyze automatically
            setTimeout(() => analyze(), 200);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Optimize failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="timing-pool">
            {/* Intro */}
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <div className="flex items-center gap-2 mb-1">
                    <Users size={14} weight="fill" className="text-fa-sky" />
                    <h2 className="font-display font-black text-xl uppercase tracking-tight">
                        {t("poolBuildTitle", lang)}
                    </h2>
                </div>
                <p className="font-mono text-[11px] text-white/60">{t("poolBuildHint", lang)}</p>
            </header>

            {/* Budget bar */}
            <BudgetBar total={budget.total} count={pool.length} lang={lang} />

            {/* Positional distribution */}
            <PositionBar
                pool={pool}
                lang={lang}
                activePos={chipPosFilter}
                onTogglePos={(pos) => setChipPosFilter((cur) => (cur === pos ? "" : pos))}
            />

            {/* Search + paste + actions */}
            <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[240px]">
                        <MagnifyingGlass size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={searchKeyDown}
                            placeholder={loadingFpl ? t("loading", lang) : t("searchPlaceholder", lang)}
                            disabled={loadingFpl}
                            data-testid="pool-search-input"
                            className="w-full bg-black/40 border border-white/15 pl-7 pr-16 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-sky disabled:opacity-40"
                        />
                        {query && (
                            <>
                                <kbd
                                    className="absolute right-7 top-1/2 -translate-y-1/2 font-mono text-[8px] uppercase tracking-widest text-white/35 border border-white/15 px-1 py-[1px] pointer-events-none"
                                    aria-hidden
                                >
                                    ESC
                                </kbd>
                                <button
                                    type="button"
                                    onClick={() => { setQuery(""); resetIndex(); }}
                                    aria-label="Zavřít vyhledávání"
                                    title="Zavřít (Esc)"
                                    data-testid="pool-search-clear"
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/45 hover:text-white p-0.5"
                                >
                                    <X size={12} weight="bold" />
                                </button>
                            </>
                        )}
                        {query && filteredCurrent.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto border border-white/15 bg-[#0d0d0d] shadow-xl">
                                {filteredCurrent.map((p, i) => (
                                    <PlayerSearchRow
                                        key={p.fpl_id}
                                        p={p}
                                        alreadyIn={pool.some((x) => x.fpl_id === p.fpl_id)}
                                        onAdd={addCurrent}
                                        highlighted={i === selectedIndex}
                                        innerRef={itemRef(i)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowPaste((v) => !v)}
                        data-testid="pool-paste-toggle"
                        className="font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1.5 hover:bg-fa-sky hover:text-black"
                    >
                        {t("pasteList", lang)}
                    </button>
                    <button
                        type="button"
                        onClick={clearPool}
                        disabled={pool.length === 0}
                        data-testid="pool-clear-btn"
                        className="font-mono text-[10px] uppercase tracking-widest text-red-300 border border-red-300/40 px-2 py-1.5 hover:bg-red-300 hover:text-black disabled:opacity-30"
                    >
                        <Trash size={10} weight="bold" className="inline mb-0.5 mr-1" />
                        {t("clearPool", lang)}
                    </button>
                    <button
                        type="button"
                        onClick={analyze}
                        disabled={pool.length < 5 || busy}
                        title={
                            pool.length < 5
                                ? `Analýza vyžaduje alespoň 5 hráčů (aktuálně ${pool.length}, max ${POOL_MAX})`
                                : ""
                        }
                        data-testid="pool-analyze-btn"
                        className="ml-auto font-mono text-[10px] uppercase tracking-widest bg-fa-yellow text-black px-3 py-1.5 hover:bg-fa-yellowHover disabled:opacity-30 disabled:cursor-not-allowed font-bold"
                    >
                        <PlayCircle size={12} weight="fill" className="inline mb-0.5 mr-1" />
                        {t("analyzePool", lang)} ({pool.length}/{POOL_MAX})
                    </button>
                    <button
                        type="button"
                        onClick={optimize}
                        disabled={pool.length < 5 || busy}
                        data-testid="pool-optimize-btn"
                        className="font-mono text-[10px] uppercase tracking-widest text-fa-mint border border-fa-mint/40 px-2 py-1.5 hover:bg-fa-mint hover:text-black disabled:opacity-30"
                    >
                        <Wrench size={10} weight="bold" className="inline mb-0.5 mr-1" />
                        {t("optimizePool", lang)}
                    </button>
                    <button
                        type="button"
                        onClick={fetchSuggestions}
                        disabled={pool.length < 2 || busy}
                        data-testid="pool-suggest-btn"
                        className="font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1.5 hover:bg-fa-sky hover:text-black disabled:opacity-30"
                    >
                        <Compass size={10} weight="bold" className="inline mb-0.5 mr-1" />
                        Doplnit pool
                    </button>
                </div>

                {showPaste && (
                    <div className="border-t border-white/10 pt-3 space-y-2">
                        <textarea
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                            rows={5}
                            placeholder={"Haaland\nSalah\nSaka\nBruno\nBowen\nWatkins\nGabriel\nRaya\nPalmer\nFoden\nEze\nMbeumo"}
                            data-testid="pool-paste-textarea"
                            className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-sky"
                        />
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[9px] text-white/40 italic flex-1">{t("pasteHint", lang)}</span>
                            <button
                                type="button"
                                onClick={importPaste}
                                disabled={!pasteText.trim() || busy}
                                data-testid="pool-paste-import"
                                className="font-mono text-[10px] uppercase tracking-widest bg-fa-sky text-black px-2 py-1 hover:bg-fa-sky/80 disabled:opacity-30 font-bold"
                            >
                                {t("importPaste", lang)}
                            </button>
                        </div>
                    </div>
                )}

                {pool.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap pt-2 border-t border-white/10" data-testid="pool-chips">
                        {pool
                            .filter((p) => !chipPosFilter || p.position === chipPosFilter)
                            .map((p) => (
                                <PoolChip key={p.fpl_id} p={p} onRemove={removeFromPool} />
                            ))}
                        {chipPosFilter && pool.every((p) => p.position !== chipPosFilter) && (
                            <span className="font-mono text-[10px] italic text-white/40">
                                Žádný hráč pozice {chipPosFilter} v poolu.
                            </span>
                        )}
                    </div>
                )}

                {/* Named list save/load — shared with Fixture Matrix */}
                <div className="pt-2 border-t border-white/10">
                    <SavedLists
                        players={pool}
                        source="pool"
                        onLoad={(ps) => setPool(ps.slice(0, POOL_MAX))}
                    />
                </div>
            </div>

            {/* Analysis output */}
            {analysis && (
                <>
                    <TypologyRow typology={analysis.dna.typology} lang={lang} />

                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-3">
                        <DnaChart dna={analysis.dna} lang={lang} />

                        {/* Report text */}
                        <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="pool-report">
                            <div className="flex items-center gap-2 mb-1">
                                <Sparkle size={12} weight="fill" className="text-fa-yellow" />
                                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                                    {t("poolReport", lang)}
                                </h3>
                            </div>
                            <p className="font-mono text-[11px] leading-relaxed text-white/85 whitespace-pre-line">
                                {analysis.report_cz}
                            </p>
                        </div>
                    </div>

                    {/* Top lists — 4 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <TopList title={t("topStart", lang)} icon={Lightning} players={analysis.top.start} lang={lang} accent="fa-yellow" testid="top-start" />
                        <TopList title={t("topWinter", lang)} icon={Snowflake} players={analysis.top.winter} lang={lang} accent="fa-sky" testid="top-winter" />
                        <TopList title={t("topRunIn", lang)} icon={Flag} players={analysis.top.runin} lang={lang} accent="fa-mint" testid="top-runin" />
                        <TopList title="Celosezonní držáci" icon={Trophy} players={analysis.top.holders} lang={lang} accent="fa-yellow" testid="top-holders" scoreKey="season_holder" />
                    </div>

                    {/* Suggestions to complete the pool */}
                    <SuggestionsBlock
                        suggestions={suggestions?.suggestions}
                        context={suggestions?.context}
                        onAdd={addSuggestion}
                        lang={lang}
                    />

                    {/* Risks */}
                    {analysis.risks?.length > 0 && (
                        <div className="border border-red-400/30 bg-red-500/[0.03] p-3" data-testid="pool-risks">
                            <div className="flex items-center gap-2 mb-2">
                                <Warning size={12} weight="fill" className="text-red-300" />
                                <h3 className="font-display font-black text-sm uppercase tracking-widest text-red-200">
                                    {t("risks", lang)}
                                </h3>
                            </div>
                            <ul className="space-y-1">
                                {analysis.risks.map((r) => (
                                    <li key={r.player.fpl_id} className="font-mono text-[10px]"
                                        data-testid={`risk-${r.player.fpl_id}`}>
                                        <span className="font-display font-black text-sm text-red-200">{r.player.display_name}</span>
                                        <span className="text-white/50 ml-2">— {r.risks.join(", ")}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Player cards — sortable + filterable */}
                    <div>
                        <div className="flex items-center justify-between gap-3 flex-wrap mt-4 mb-2">
                            <h3 className="font-display font-black text-lg uppercase tracking-widest">
                                {t("playerCards", lang)}
                            </h3>
                            <div className="flex items-center gap-1.5 flex-wrap font-mono text-[10px] uppercase tracking-widest">
                                <span className="text-white/45">Filtr:</span>
                                {["", "GKP", "DEF", "MID", "FWD"].map((p) => (
                                    <button
                                        key={p || "all"}
                                        type="button"
                                        onClick={() => setPosFilter(p)}
                                        data-testid={`pool-card-filter-${p || "all"}`}
                                        className={`px-2 py-1 border ${posFilter === p ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                                    >
                                        {p || t("allPositions", lang)}
                                    </button>
                                ))}
                                <span className="text-white/45 ml-2">Řadit:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    data-testid="pool-card-sort-by"
                                    className="bg-black/40 border border-white/15 px-2 py-1 focus:outline-none focus:border-fa-sky"
                                >
                                    <option value="season_dna">Season DNA</option>
                                    <option value="consistency">Konzistence</option>
                                    <option value="explosiveness">Výbušnost</option>
                                    <option value="value">Hodnota</option>
                                    <option value="season_holder">Držák sezóny</option>
                                    <option value="current_price">Cena</option>
                                    <option value="display_name">Jméno (A–Z)</option>
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setSortDir((d) => d === "desc" ? "asc" : "desc")}
                                    data-testid="pool-card-sort-dir"
                                    className="px-2 py-1 border border-white/15 hover:border-fa-sky text-fa-sky"
                                    title={sortDir === "desc" ? "Sestupně (kliknutím vzestupně)" : "Vzestupně (kliknutím sestupně)"}
                                >
                                    {sortDir === "desc" ? "▼ DESC" : "▲ ASC"}
                                </button>
                            </div>
                        </div>

                        <MetricsGlossary lang={lang} />

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="pool-player-cards">
                            {sortedPlayers.map((p) => (
                                <PlayerCard key={p.fpl_id} p={p} lang={lang} />
                            ))}
                            {sortedPlayers.length === 0 && (
                                <div className="col-span-full font-mono text-[10px] text-white/40 italic border border-dashed border-white/15 p-4 text-center">
                                    Žádní hráči pro filtr {posFilter || "—"}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
